'use client';

/**
 * ListDetailClient — unified list detail + contextual inline editing island.
 *
 * Design principles (from plan):
 *   • No full-page mode switch — metadata edits are inline on tap/click.
 *   • Autosave for title / description / visibility — fires on blur / tap with
 *     optimistic update and an undo toast.
 *   • Explicit save for item mutations — reorder + removals are batched and
 *     committed when the user taps [Done] in item-edit mode.
 *   • Collaborators visible to everyone via an avatar stack in the header;
 *     managing them is behind a Dialog.
 *   • Card dimensions never change between modes — only the right-side
 *     affordance swaps (status pills ↔ remove button + drag handle).
 *
 * State model:
 *   Metadata  — optimistic, autosave on blur/tap, undo-able via toast
 *   Items     — snapshot-based; Cancel reverts without any API call
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  lazy,
  Suspense,
  useMemo,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import {
  GripVertical,
  X,
  Plus,
  Loader2,
  Users,
  Pencil,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CategoryEmoji, getCategoryColor } from '@/components/favely/category-emoji';
import { ItemCard } from '@/components/favely/item-card';
import { ListProgressBar } from '@/components/collections/list-progress-bar';
import { CollaboratorManagePanel } from '@/components/collections/collaborator-manage-panel';
import { ListActionBar } from '@/components/collections/list-action-bar';
import {
  updateCollection,
  createItem,
  addCollectionItem,
  removeCollectionItem,
  reorderCollectionItems,
  fetchItemNetworkCounts,
} from '@/lib/api';
import { getItemSubtitle } from '@/lib/item-subtitle';
import type { CollectionItemData } from '@/types/collection';
import type { SearchResult } from '@/lib/api';
import type { ItemSearchPanelProps, ProviderConf, ResolvedCategory, ProviderValue } from '@/components/quick-add/item-search-panel';

// ── Lazy-load ItemSearchPanel so the search+dnd bundle is deferred until
//    the user first taps "✎ Reorder" ──────────────────────────────────────────
const ItemSearchPanel = dynamic(
  () => import('@/components/quick-add/item-search-panel').then((m) => m.ItemSearchPanel),
  { ssr: false }
) as React.ComponentType<ItemSearchPanelProps>;

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeDate(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

// ── Category slug → provider chip mapping ────────────────────────────────────
const CATEGORY_SLUG_TO_PROVIDER: Record<string, ProviderValue> = {
  'movie':      'movie',
  'tv-show':    'tv',
  'book':       'book',
  'podcast':    'podcast',
  'restaurant': 'restaurant',
  'place':      'place',
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ContributorUser {
  _id: string;
  displayName?: string;
  username?: string;
  imageUrl?: string;
}

export interface CollaboratorEntry {
  userId?: string;
  email?: string;
  role: string;
  status: string;
  user?: ContributorUser | null;
}

export interface EditorItem {
  /** collectionItem._id */
  id: string;
  title: string;
  thumbnail?: string;
  categorySlug?: string;
  properties?: Array<{ key: string; value: string }>;
  /** The underlying item._id (needed for add / remove API calls) */
  itemId: string;
}

type Visibility = 'public' | 'unlisted' | 'private';

interface ListDetailClientProps {
  collection: {
    _id: string;
    title: string;
    description?: string;
    visibility: Visibility;
    ownerId: string;
    updatedAt?: string;
  };
  /** Pre-serialised items (SSR, position-sorted) */
  initialItems: EditorItem[];
  primaryCategory: string | null;
  ownerUser: ContributorUser | null;
  /** All accepted collaborators (owner + editors + admins + viewers) */
  collaborators: CollaboratorEntry[];
  /** Whether the viewer can edit (owner or editor/admin collaborator) */
  canEdit: boolean;
  /** Whether the viewer is the owner or an admin collaborator */
  isOwnerOrAdmin: boolean;
  /** MongoDB userId of the authenticated viewer, or null */
  mongoUserId: string | null;
  /** Whether the viewer is the collection owner */
  isOwner: boolean;
  /** Initial pin state */
  isPinned: boolean;
  /** Network friend count per itemId — populated server-side for authenticated users */
  initialNetworkCounts?: Record<string, number>;
}

// ── Visibility cycling helpers ────────────────────────────────────────────────

const VISIBILITY_ORDER: Visibility[] = ['public', 'unlisted', 'private'];

function nextVisibility(v: Visibility): Visibility {
  return VISIBILITY_ORDER[(VISIBILITY_ORDER.indexOf(v) + 1) % VISIBILITY_ORDER.length];
}

const VISIBILITY_STYLE: Record<Visibility, string> = {
  public:   'bg-success/10 text-success border-success/20',
  unlisted: 'bg-accent/10 text-accent-foreground border-accent/20',
  private:  'bg-muted text-muted-foreground border-border',
};

// ── Contributor avatar stack ──────────────────────────────────────────────────

interface ContributorStackProps {
  owner: ContributorUser | null;
  /** Accepted collaborators with editor/admin/viewer roles */
  collaborators: CollaboratorEntry[];
  /** Opens the manage-collaborators dialog */
  onManage: () => void;
}

function ContributorStack({ owner, collaborators, onManage }: ContributorStackProps) {
  const accepted = collaborators.filter((c) => c.status === 'accepted' && c.user);
  // Build ordered list: owner first, then accepted collaborators (max 5 total)
  const allUsers: ContributorUser[] = [
    ...(owner ? [owner] : []),
    ...accepted.map((c) => c.user!),
  ].slice(0, 5);
  const overflowCount = Math.max(
    0,
    (owner ? 1 : 0) + accepted.length - allUsers.length
  );

  const ownerName = owner?.displayName ?? owner?.username ?? 'Owner';

  return (
    <div className="flex items-center justify-between mt-2">
      {/* Left: overlapping avatars + owner name */}
      <div className="flex items-center gap-2">
        <div className="flex -space-x-2">
          {allUsers.map((u) => {
            const initials = (u.displayName ?? u.username ?? '?').charAt(0).toUpperCase();
            return (
              <Avatar key={u._id} className="h-6 w-6 ring-2 ring-background" title={u.displayName ?? u.username}>
                {u.imageUrl && <AvatarImage src={u.imageUrl} alt={initials} />}
                <AvatarFallback className="text-[10px] bg-secondary text-secondary-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
            );
          })}
          {overflowCount > 0 && (
            <div className="h-6 w-6 rounded-full ring-2 ring-background bg-muted flex items-center justify-center text-[10px] font-medium text-muted-foreground">
              +{overflowCount}
            </div>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {ownerName}
          {accepted.length > 0 && ` and ${accepted.length} other${accepted.length > 1 ? 's' : ''}`}
        </span>
      </div>

      {/* Right: Collab button — clearly interactive, always visible */}
      <button
        type="button"
        onClick={onManage}
        className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-border bg-muted/50 hover:bg-muted transition-colors"
        aria-label="Manage collaborators"
      >
        <Users className="h-3 w-3" />
        Collab
      </button>
    </div>
  );
}

// ── Inline editable text ──────────────────────────────────────────────────────

interface InlineTitleProps {
  value: string;
  onSave: (next: string) => void;
  canEdit: boolean;
  isSaving: boolean;
}

function InlineTitle({ value, onSave, canEdit, isSaving }: InlineTitleProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  // Keep draft in sync if parent value changes (e.g. undo)
  useEffect(() => { setDraft(value); }, [value]);

  if (!canEdit) {
    return <h1 className="text-2xl font-bold tracking-tight">{value}</h1>;
  }

  if (!editing) {
    return (
      <h1
        className="text-2xl font-bold tracking-tight cursor-text hover:underline decoration-dashed underline-offset-4 decoration-muted-foreground/40 transition-all group"
        onClick={() => setEditing(true)}
        title="Tap to edit title"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
      >
        {value}
        {isSaving && <Loader2 className="inline h-3.5 w-3.5 ml-1.5 animate-spin text-muted-foreground" />}
      </h1>
    );
  }

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); if (draft.trim() && draft.trim() !== value) onSave(draft.trim()); else setDraft(value); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
      maxLength={200}
      className="text-2xl font-bold h-auto py-0.5 px-1 border-0 border-b-2 border-primary rounded-none bg-transparent focus-visible:ring-0 shadow-none"
    />
  );
}

interface InlineDescriptionProps {
  value: string;
  onSave: (next: string) => void;
  canEdit: boolean;
}

function InlineDescription({ value, onSave, canEdit }: InlineDescriptionProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (!canEdit && !value) return null;

  if (!canEdit) {
    return <p className="text-muted-foreground mt-1">{value}</p>;
  }

  if (!editing) {
    return (
      <p
        className="text-muted-foreground mt-1 cursor-text hover:underline decoration-dashed underline-offset-4 decoration-muted-foreground/40 transition-all min-h-[1.25rem]"
        onClick={() => setEditing(true)}
        role="button"
        tabIndex={0}
        title="Tap to edit description"
        onKeyDown={(e) => e.key === 'Enter' && setEditing(true)}
      >
        {value || <span className="italic opacity-50">Add description…</span>}
      </p>
    );
  }

  return (
    <Textarea
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        setEditing(false);
        const next = draft.trim();
        if (next !== value.trim()) onSave(next);
        else setDraft(value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
      rows={2}
      maxLength={2000}
      placeholder="Add description…"
      className="mt-1 text-sm text-muted-foreground resize-none"
    />
  );
}

// ── Sortable item row (item-edit mode) ────────────────────────────────────────

interface SortableEditorRowProps {
  item: EditorItem;
  onRemove: (id: string) => void;
  index: number;
  subtitle?: string | null;
}

function SortableEditorRow({ item, onRemove, index, subtitle }: SortableEditorRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  const accentColor = getCategoryColor(item.categorySlug);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-stretch rounded-lg border-2 border-border bg-card overflow-hidden"
    >
      {/* Category color strip */}
      <div className="w-1 shrink-0" style={{ backgroundColor: accentColor }} aria-hidden />

      {/* Rank + drag handle stacked vertically, tight against the thumbnail */}
      <div className="shrink-0 flex flex-col items-center justify-center gap-2 px-1">
        <span className="text-[10px] font-semibold text-muted-foreground/60 tabular-nums leading-none">
          {index + 1}
        </span>
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing touch-none text-muted-foreground/40 hover:text-muted-foreground transition-colors"
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>

      {/* Thumbnail — same size & padding as ItemCard view mode */}
      <div className="flex items-center px-1 py-3 shrink-0">
        {item.thumbnail ? (
          <Image
            src={item.thumbnail}
            alt=""
            width={48}
            height={48}
            className="h-12 w-12 rounded-md object-cover"
          />
        ) : (
          <div
            className="h-12 w-12 rounded-md flex items-center justify-center"
            style={{ backgroundColor: `${accentColor}20` }}
          >
            <CategoryEmoji category={item.categorySlug ?? 'other'} size="sm" />
          </div>
        )}
      </div>

      {/* Title + subtitle */}
      <div className="flex-1 min-w-0 flex items-center py-3 pl-2">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug truncate">{item.title}</p>
          {subtitle && <p className="text-xs text-muted-foreground truncate mt-0.5">{subtitle}</p>}
        </div>
      </div>

      {/* Remove button — well-padded on the right */}
      <button
        type="button"
        onClick={() => onRemove(item.id)}
        className="shrink-0 self-center px-4 py-3 text-muted-foreground hover:text-destructive transition-colors"
        aria-label={`Remove ${item.title}`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Add panel (item-edit mode) ────────────────────────────────────────────────

interface AddPanelProps {
  collectionId: string;
  onItemAdded: (item: EditorItem) => void;
  initialProvider?: ProviderValue;
}

function AddPanel({ collectionId, onItemAdded, initialProvider }: AddPanelProps) {
  const [addingKey, setAddingKey] = useState<string | null>(null);

  async function handleSelect(
    result: SearchResult,
    providerConf: ProviderConf | null,
    effectiveCategory: ResolvedCategory | null
  ) {
    if (!effectiveCategory || addingKey) return;
    setAddingKey(result.externalId);
    try {
      const { item } = await createItem({
        title: result.title,
        categoryId: effectiveCategory._id,
        scope: 'private' as const,
        ...(result.thumbnail ? { thumbnail: result.thumbnail } : {}),
        ...(result.properties?.length ? { properties: result.properties as any } : {}),
        ...(providerConf ? { sourceRef: { provider: providerConf.apiProvider, externalId: result.externalId } } : {}),
        ...(providerConf?.value === 'tv' ? { mediaType: 'tv' as const } : {}),
        ...(providerConf?.value === 'movie' ? { mediaType: 'movie' as const } : {}),
      });
      const { collectionItem } = await addCollectionItem(collectionId, { itemId: item._id });
      onItemAdded({
        id: collectionItem._id,
        title: item.title,
        thumbnail: item.thumbnail,
        categorySlug: providerConf?.categorySlug,
        properties: result.properties ?? [],
        itemId: item._id,
      });
      toast.success(`"${item.title}" added`);
    } catch (err: any) {
      if (err.message?.includes('already exists in this collection')) {
        toast.info(`"${result.title}" is already in this list`);
      } else {
        toast.error(err.message || 'Failed to add item');
      }
    } finally {
      setAddingKey(null);
    }
  }

  return (
    <div className="rounded-xl border bg-muted/30 p-4 mt-3">
      <ItemSearchPanel
        onSelect={handleSelect}
        renderRowSuffix={(result) =>
          addingKey === result.externalId
            ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
            : null
        }
        suppressHighlight={(result) => addingKey === result.externalId}
        initialProvider={initialProvider}
      />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ListDetailClient({
  collection,
  initialItems,
  primaryCategory,
  ownerUser,
  collaborators,
  canEdit,
  isOwnerOrAdmin,
  mongoUserId,
  isOwner,
  isPinned,
  initialNetworkCounts,
}: ListDetailClientProps) {
  const router = useRouter();

  // ── Metadata — optimistic + autosave ─────────────────────────────────────
  const [title, setTitle] = useState(collection.title);
  const [description, setDescription] = useState(collection.description ?? '');
  const [visibility, setVisibility] = useState<Visibility>(collection.visibility);
  const [isSavingMeta, setIsSavingMeta] = useState(false);

  // ── Items ─────────────────────────────────────────────────────────────────
  const [items, setItems] = useState<EditorItem[]>(initialItems);
  const [itemEditMode, setItemEditMode] = useState(false);
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());
  const [pendingReorder, setPendingReorder] = useState<string[] | null>(null);
  const [isSavingItems, setIsSavingItems] = useState(false);
  // Snapshot taken when entering item-edit mode — used to revert on Cancel
  const [itemsSnapshot, setItemsSnapshot] = useState<EditorItem[]>(initialItems);

  // ── Collaborator dialog ────────────────────────────────────────────────────
  const [collabDialogOpen, setCollabDialogOpen] = useState(false);

  // ── Network counts — fetched client-side after mount ─────────────────────
  // The server no longer blocks on this; we fire the batch request after hydration
  // so the list renders immediately and counts appear ~300ms later.
  const itemIds = useMemo(() => initialItems.map((i) => i.itemId), [initialItems]);
  const { data: networkCountsData } = useQuery({
    queryKey: ['network-counts', collection._id],
    queryFn: () => fetchItemNetworkCounts(itemIds),
    enabled: !!mongoUserId && itemIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 min — matches Redis TTL
  });
  const networkCounts = networkCountsData?.counts ?? initialNetworkCounts ?? {};

  // ── dnd-kit sensors ───────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // ── Metadata: autosave helpers ────────────────────────────────────────────

  /**
   * Persist a metadata patch to the server with optimistic update + undo toast.
   * The caller has already applied the optimistic local state before calling this.
   */
  async function saveMeta(
    patch: Partial<{ title: string; description: string; visibility: Visibility }>,
    prevValues: Partial<{ title: string; description: string; visibility: Visibility }>,
    label: string
  ) {
    setIsSavingMeta(true);
    try {
      await updateCollection(collection._id, patch);
      toast.success(`${label} updated`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            // Revert local state
            if (prevValues.title !== undefined) setTitle(prevValues.title);
            if (prevValues.description !== undefined) setDescription(prevValues.description);
            if (prevValues.visibility !== undefined) setVisibility(prevValues.visibility);
            // Re-persist the revert
            try { await updateCollection(collection._id, prevValues); } catch {/* ignore */}
          },
        },
      });
    } catch (err: any) {
      // On error, revert local state
      if (prevValues.title !== undefined) setTitle(prevValues.title);
      if (prevValues.description !== undefined) setDescription(prevValues.description);
      if (prevValues.visibility !== undefined) setVisibility(prevValues.visibility);
      toast.error(err.message ?? 'Failed to save');
    } finally {
      setIsSavingMeta(false);
    }
  }

  function handleTitleSave(next: string) {
    const prev = title;
    setTitle(next);
    saveMeta({ title: next }, { title: prev }, 'Title');
  }

  function handleDescriptionSave(next: string) {
    const prev = description;
    setDescription(next);
    saveMeta({ description: next }, { description: prev }, 'Description');
  }

  async function handleVisibilityCycle() {
    if (!canEdit) return;
    const next = nextVisibility(visibility);
    const prev = visibility;
    setVisibility(next); // optimistic
    saveMeta({ visibility: next }, { visibility: prev }, `Visibility`);
  }

  // ── Items: item-edit mode controls ───────────────────────────────────────

  function enterItemEditMode() {
    setItemsSnapshot([...items]);
    setPendingRemovals(new Set());
    setPendingReorder(null);
    setItemEditMode(true);
  }

  function cancelItemEditMode() {
    // Revert to snapshot, no API call
    setItems(itemsSnapshot);
    setPendingRemovals(new Set());
    setPendingReorder(null);
    setItemEditMode(false);
  }

  async function saveItemEdits() {
    if (isSavingItems) return;
    setIsSavingItems(true);
    try {
      // 1. Removals (parallel)
      if (pendingRemovals.size > 0) {
        await Promise.all(
          [...pendingRemovals].map((id) => removeCollectionItem(collection._id, id))
        );
      }

      // 2. Reorder (after removals so deleted IDs are excluded)
      if (pendingReorder !== null) {
        const validIds = pendingReorder.filter((id) => !pendingRemovals.has(id));
        if (validIds.length > 1) {
          await reorderCollectionItems(collection._id, validIds);
        }
      }

      // Commit the local state as the new snapshot
      const committed = items.filter((i) => !pendingRemovals.has(i.id));
      setItems(committed);
      setItemsSnapshot(committed);
      setPendingRemovals(new Set());
      setPendingReorder(null);
      setItemEditMode(false);
      toast.success('List updated');
    } catch (err: any) {
      toast.error(err.message ?? 'Failed to save changes');
    } finally {
      setIsSavingItems(false);
    }
  }

  // ── Items: drag-to-reorder ────────────────────────────────────────────────

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIndex = prev.findIndex((i) => i.id === active.id);
      const newIndex = prev.findIndex((i) => i.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      setPendingReorder(reordered.map((i) => i.id));
      return reordered;
    });
  }

  // ── Items: remove (local only until Done) ────────────────────────────────

  function handleRemove(collectionItemId: string) {
    setPendingRemovals((prev) => new Set([...prev, collectionItemId]));
    setItems((prev) => prev.filter((i) => i.id !== collectionItemId));
    setPendingReorder((prev) =>
      prev ? prev.filter((id) => id !== collectionItemId) : null
    );
  }

  // ── Items: add (persisted immediately to server) ─────────────────────────

  const handleItemAdded = useCallback((item: EditorItem) => {
    setItems((prev) => [...prev, item]);
    setPendingReorder((prev) => (prev ? [...prev, item.id] : null));
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────

  // Visible items (excludes pending removals so the list stays consistent)
  const visibleItems = items.filter((i) => !pendingRemovals.has(i.id));

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-3xl">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="space-y-1">
        <div className="flex items-start gap-3">
          <CategoryEmoji category={primaryCategory} size="lg" className="mt-1 shrink-0" />
          <div className="flex-1 min-w-0">
            <InlineTitle
              value={title}
              onSave={handleTitleSave}
              canEdit={canEdit}
              isSaving={isSavingMeta}
            />
            <InlineDescription
              value={description}
              onSave={handleDescriptionSave}
              canEdit={canEdit}
            />

            {/* Contributors row */}
            <ContributorStack
              owner={ownerUser}
              collaborators={collaborators}
              onManage={() => setCollabDialogOpen(true)}
            />

            {/* Visibility badge — cycles on tap for editors */}
            <div className="mt-2">
              {canEdit ? (
                <button
                  type="button"
                  onClick={handleVisibilityCycle}
                  title={`Visibility: ${visibility} — tap to change`}
                  className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize border transition-all hover:opacity-80 active:scale-95 ${
                    VISIBILITY_STYLE[visibility]
                  }`}
                >
                  {visibility}
                </button>
              ) : (
                <span
                  className={`text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize border ${
                    VISIBILITY_STYLE[visibility]
                  }`}
                >
                  {visibility}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Action bar ─────────────────────────────────────────────────────── */}
      <ListActionBar
        collectionId={collection._id}
        listTitle={title}
        isOwner={isOwner}
        canCopy={!!mongoUserId && !isOwner && visibility !== 'private'}
        canPin={!!mongoUserId && !isOwner}
        initialPinned={isPinned}
      />

      {/* ── Items section ──────────────────────────────────────────────────── */}
      <section>
        {/* Section header */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Items ({visibleItems.length})</h2>
          {canEdit && !itemEditMode && (
            <button
              type="button"
              onClick={enterItemEditMode}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
          {canEdit && itemEditMode && (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelItemEditMode}
                disabled={isSavingItems}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveItemEdits}
                disabled={isSavingItems}
              >
                {isSavingItems ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Done'}
              </Button>
            </div>
          )}
        </div>

        {/* Progress bar (signed-in, ≥2 items, not in edit mode) */}
        {mongoUserId && visibleItems.length >= 2 && !itemEditMode && (
          <div className="mb-4">
            <ListProgressBar itemIds={visibleItems.map((ci) => ci.itemId)} />
          </div>
        )}

        {/* Empty state */}
        {visibleItems.length === 0 && !itemEditMode && (
          <div className="text-center py-12 rounded-lg border border-dashed border-border">
            <p className="text-muted-foreground">No items yet.</p>
            {canEdit && (
              <Button
                size="sm"
                variant="ghost"
                className="mt-2"
                onClick={enterItemEditMode}
              >
                Add your first item
              </Button>
            )}
          </div>
        )}

        {/* ── View mode: ItemCards ──────────────────────────────────────────── */}
        {!itemEditMode && visibleItems.length > 0 && (
          <div className="space-y-2">
            {visibleItems.map((item) => (
              <ItemCard
                key={item.id}
                itemId={item.itemId}
                title={item.title}
                subtitle={getItemSubtitle(item.categorySlug, item.properties ?? []) ?? undefined}
                category={item.categorySlug ?? primaryCategory}
                thumbnail={item.thumbnail}
                showStatus={!!mongoUserId}
                networkCount={networkCounts[item.itemId] ?? 0}
              />
            ))}
          </div>
        )}

        {/* ── Item-edit mode: sortable rows ─────────────────────────────────── */}
        {itemEditMode && (
          <>
            {visibleItems.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={visibleItems.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2">
                    {visibleItems.map((item, idx) => (
                      <SortableEditorRow
                        key={item.id}
                        item={item}
                        index={idx}
                        onRemove={handleRemove}
                        subtitle={getItemSubtitle(item.categorySlug, item.properties ?? [])}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="rounded-lg border border-dashed py-8 text-center text-sm text-muted-foreground">
                No items. Add some below.
              </div>
            )}

            {/* Add item panel — always visible in item-edit mode */}
            <AddPanel
              collectionId={collection._id}
              onItemAdded={handleItemAdded}
              initialProvider={CATEGORY_SLUG_TO_PROVIDER[primaryCategory ?? ''] ?? 'movie'}
            />
          </>
        )}
      </section>

      {/* ── Last updated ───────────────────────────────────────────────────── */}
      {collection.updatedAt && (
        <p className="text-[11px] text-muted-foreground/50 text-center pb-2">
          Updated {relativeDate(collection.updatedAt)}
        </p>
      )}

      {/* ── Collaborators Dialog ────────────────────────────────────────────── */}
      <Dialog open={collabDialogOpen} onOpenChange={setCollabDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Collaborators</DialogTitle>
          </DialogHeader>
          {isOwnerOrAdmin && mongoUserId ? (
            /* Editors/owners get the full manage panel */
            <CollaboratorManagePanel
              collectionId={collection._id}
              canManage={isOwnerOrAdmin}
              currentUserId={mongoUserId}
              initialCollaborators={collaborators as any}
            />
          ) : (
            /* Viewers get a read-only list */
            <ul className="space-y-3">
              {[ownerUser, ...collaborators.filter((c) => c.status === 'accepted').map((c) => c.user)]
                .filter(Boolean)
                .map((u: any) => (
                  <li key={u._id}>
                    <Link
                      href={`/u/${u._id}`}
                      className="flex items-center gap-3 rounded-md hover:bg-muted/50 -mx-1 px-1 py-1 transition-colors"
                      onClick={() => setCollabDialogOpen(false)}
                    >
                      <Avatar className="h-8 w-8 shrink-0">
                        {u.imageUrl && <AvatarImage src={u.imageUrl} />}
                        <AvatarFallback className="text-xs">
                          {(u.displayName ?? u.username ?? '?').charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{u.displayName ?? u.username}</p>
                        {u.username && <p className="text-xs text-muted-foreground">@{u.username}</p>}
                      </div>
                    </Link>
                  </li>
                ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
